import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  ClipboardCheck, Plus, Loader2, X, Trash2, FileText, Camera, MapPin, Clock,
  Home, DoorOpen, DoorClosed, Save, ChevronLeft, Package, Gauge, KeyRound, LayoutTemplate,
  ListChecks, PenLine, Sparkles, Scale, CheckCircle2, Eraser, Video, QrCode, Copy, Send, Search, Download, Pencil,
} from "lucide-react";
import api from "../../lib/api";
import { SavedSignatureButton } from "../../components/dashboard/SavedSignatureButton";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import EdlDispute from "../../components/dashboard/EdlDispute";

const fileUrl = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const inp = "w-full px-3 py-2 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep";
const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-brand-green/70 mb-1 block">{label}</span>
    {children}
  </label>
);

const EMPTY_ROOM = { name: "", condition: "", sols: "", murs: "", plafond: "", equipment: "", observations: "", photos: [] };
const EMPTY_INV = { item: "", room: "", quantity: 1, condition: "", observations: "" };

// Récupère la géolocalisation du navigateur (résolue même en cas d'échec)
function getGeo() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  });
}

export default function EtatsDesLieux({ readOnly = false, initialEdlId = null, onCloseView = null }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [conditions, setConditions] = useState(["Neuf", "Bon état", "État d'usage", "Mauvais état", "À rénover"]);
  const [templates, setTemplates] = useState([]);
  const [edl, setEdl] = useState(null); // éditeur ouvert
  const [busy, setBusy] = useState(false);
  const [uploadingRoom, setUploadingRoom] = useState(null);
  const location = useLocation();
  const { user } = useAuth();
  const shareView = async (eid) => {
    try { const r = await api.post(`/edl/${eid}/share-view`, { origin_url: window.location.origin }); await navigator.clipboard.writeText(r.data.url); toast.success("Lien de consultation copié — partageable avec le locataire et le propriétaire"); }
    catch { toast.error("Impossible de générer le lien"); }
  };
  const [protocolSteps, setProtocolSteps] = useState([]);
  const [vetCats, setVetCats] = useState(["Autre"]);
  const [signParty, setSignParty] = useState(null); // party being signed
  const [comparing, setComparing] = useState(false);
  const [allocBusy, setAllocBusy] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(null);
  const [videoProgress, setVideoProgress] = useState(0);
  const [worksScale, setWorksScale] = useState([]);
  const [exits, setExits] = useState(null);
  const [showExits, setShowExits] = useState(false);
  const [fOwner, setFOwner] = useState("");
  const [fProperty, setFProperty] = useState("");
  const [fTenant, setFTenant] = useState("");
  const [fType, setFType] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fSearch, setFSearch] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const [selected, setSelected] = useState(new Set());
  const [moduleCfg, setModuleCfg] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subCycle, setSubCycle] = useState("monthly");
  const [clients, setClients] = useState({ owners: [], tenants: [], properties: [] });
  const [pendingMissionId, setPendingMissionId] = useState(null);
  const openedParam = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/edl"); setList(r.data || []); }
    catch { toast.error("Impossible de charger les états des lieux"); }
    finally { setLoading(false); }
    api.get("/edl/exits-dashboard").then((r) => setExits(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/edl/module-config").then((r) => {
      setModuleCfg(r.data);
      setManualMode(!!r.data.allow_independent && !r.data.allow_integrated);
    }).catch(() => {});
    api.get("/edl/clients").then((r) => setClients(r.data || { owners: [], tenants: [], properties: [] })).catch(() => {});
    api.get("/properties").then((r) => setProperties(r.data || [])).catch(() => {});
    api.get("/edl/options").then((r) => setConditions(r.data.conditions || conditions)).catch(() => {});
    api.get("/edl/templates").then((r) => setTemplates(r.data || [])).catch(() => {});
    api.get("/edl/protocol").then((r) => setProtocolSteps(r.data.steps || [])).catch(() => {});
    api.get("/edl/settings").then((r) => { setVetCats((r.data.vetuste_grid || []).map((g) => g.category)); setWorksScale(r.data.works_scale || []); }).catch(() => {});
    api.get("/edl/exits-dashboard").then((r) => setExits(r.data)).catch(() => {});
    // Ouverture directe via ?edl=<id> (ex. depuis le tableau de bord référent)
    const params = new URLSearchParams(location.search);
    const eid = initialEdlId || params.get("edl");
    if (eid && !openedParam.current) { openedParam.current = true; openEdl(eid); }
    // Pré-remplissage depuis le carnet clients (référent) via location.state.prefill
    if (location.state?.prefill && !openedParam.current) {
      openedParam.current = true;
      newEdl(location.state.prefill);
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line
  }, []);

  const newEdl = async (prefill = null) => {
    // S'assure que la liste des biens est chargée (évite une race au 1er clic)
    let props = properties;
    if (!props.length) {
      try { props = (await api.get("/properties")).data || []; setProperties(props); } catch {}
    }
    if (prefill) {
      // Démarrage pré-rempli depuis le carnet ({kind,name,email}) ou depuis une mission référent (champs explicites)
      let rooms = [];
      try { const r = await api.get(`/edl/default-rooms${prefill.property_id ? `?property_id=${prefill.property_id}` : ""}`); rooms = r.data.rooms || []; } catch {}
      setManualMode(!prefill.property_id);
      if (prefill.mission_id) setPendingMissionId(prefill.mission_id);
      setEdl({
        property_id: prefill.property_id || "",
        property_name: prefill.property_name || (prefill.kind === "property" ? prefill.name : ""),
        type: prefill.type || "entree", furnished: false,
        tenant_name: prefill.tenant_name || (prefill.kind === "tenant" ? prefill.name : ""),
        tenant_email: prefill.tenant_email || (prefill.kind === "tenant" ? (prefill.email || "") : ""),
        owner_name: prefill.owner_name || (prefill.kind === "owner" ? prefill.name : ""),
        referent_name: prefill.referent_name || "", referent_email: prefill.referent_email || "",
        edl_date: new Date().toISOString().slice(0, 10), status: "draft",
        rooms, meters: { electricite: "", gaz: "", eau_froide: "", eau_chaude: "" },
        keys: { cles: "", badges: "", boite_lettres: "" }, inventory: [], general_observations: "",
      });
      toast.success(prefill.mission_id ? "État des lieux pré-rempli — enregistrez pour le relier à la mission" : `Nouvel état des lieux pré-rempli depuis « ${prefill.name} »`);
      return;
    }
    const propId = props[0]?.id || "";
    let rooms = [];
    try { const r = await api.get(`/edl/default-rooms${propId ? `?property_id=${propId}` : ""}`); rooms = r.data.rooms || []; } catch {}
    const p = props.find((x) => x.id === propId);
    setEdl({
      property_id: propId, property_name: p?.name || "", type: "entree", furnished: false,
      tenant_name: p?.tenant || "", owner_name: p?.owner_name || "", referent_name: "", referent_email: "",
      edl_date: new Date().toISOString().slice(0, 10), status: "draft",
      rooms, meters: { electricite: "", gaz: "", eau_froide: "", eau_chaude: "" },
      keys: { cles: "", badges: "", boite_lettres: "" }, inventory: [], general_observations: "",
    });
  };

  const openEdl = async (id) => {
    try { const r = await api.get(`/edl/${id}`); setEdl(r.data); }
    catch { toast.error("Ouverture impossible"); }
  };

  const subscribeEdl = async () => {
    setSubscribing(true);
    try {
      const r = await api.post("/edl/subscribe", { cycle: subCycle, origin_url: window.location.origin });
      if (r.data.free) {
        toast.success("Offre États des lieux activée");
        const cfg = await api.get("/edl/module-config");
        setModuleCfg(cfg.data);
        setManualMode(!!cfg.data.allow_independent && !cfg.data.allow_integrated);
        load();
      } else if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Activation impossible"); }
    finally { setSubscribing(false); }
  };

  const onPropChange = async (pid) => {
    const p = properties.find((x) => x.id === pid);
    let rooms = edl.rooms;
    // Si aucune pièce personnalisée encore, recharge le modèle du bien
    if (!edl.rooms || edl.rooms.every((r) => !r.condition && !r.observations && !(r.photos || []).length)) {
      try { const r = await api.get(`/edl/default-rooms?property_id=${pid}`); rooms = r.data.rooms || []; } catch {}
    }
    setEdl({ ...edl, property_id: pid, property_name: p?.name || "", tenant_name: p?.tenant || edl.tenant_name, owner_name: p?.owner_name || edl.owner_name, rooms });
  };

  const setRoom = (i, patch) => setEdl((cur) => ({ ...cur, rooms: cur.rooms.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  const addRoom = () => setEdl((cur) => ({ ...cur, rooms: [...cur.rooms, { ...EMPTY_ROOM, name: "Nouvelle pièce" }] }));
  const removeRoom = (i) => setEdl((cur) => ({ ...cur, rooms: cur.rooms.filter((_, idx) => idx !== i) }));

  const capturePhoto = async (i, file) => {
    if (!file) return;
    setUploadingRoom(i);
    try {
      const geo = await getGeo();
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/edl/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const photo = {
        url: r.data.url, label: "",
        lat: geo?.lat ?? null, lng: geo?.lng ?? null, accuracy: geo?.accuracy ?? null,
        captured_at: new Date().toLocaleString("fr-FR"),
      };
      setEdl((cur) => ({ ...cur, rooms: cur.rooms.map((rm, idx) => (idx === i ? { ...rm, photos: [...(rm.photos || []), photo] } : rm)) }));
      toast.success(geo ? "Photo ajoutée (géolocalisée)" : "Photo ajoutée (géolocalisation indisponible)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'upload"); }
    finally { setUploadingRoom(null); }
  };
  const removePhoto = (i, pi) => setEdl((cur) => ({ ...cur, rooms: cur.rooms.map((rm, idx) => (idx === i ? { ...rm, photos: (rm.photos || []).filter((_, x) => x !== pi) } : rm)) }));

  const setInv = (i, patch) => setEdl((cur) => ({ ...cur, inventory: cur.inventory.map((it, idx) => (idx === i ? { ...it, ...patch } : it)) }));
  const addInv = () => setEdl((cur) => ({ ...cur, inventory: [...(cur.inventory || []), { ...EMPTY_INV }] }));
  const removeInv = (i) => setEdl((cur) => ({ ...cur, inventory: cur.inventory.filter((_, idx) => idx !== i) }));

  const save = async (silent = false) => {
    setBusy(true);
    try {
      const payload = { ...edl };
      let saved;
      if (edl.id) saved = (await api.put(`/edl/${edl.id}`, payload)).data;
      else saved = (await api.post("/edl", payload)).data;
      setEdl(saved);
      if (!silent) toast.success("État des lieux enregistré");
      if (pendingMissionId && saved?.id) {
        try { await api.post(`/referents/missions/${pendingMissionId}/link-edl`, { edl_id: saved.id }); toast.success("État des lieux relié à la mission"); } catch {}
        setPendingMissionId(null);
      }
      load();
      return saved;
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    const saved = await save(true);
    if (!saved?.id) return;
    try {
      const r = await api.get(`/edl/${saved.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `etat-des-lieux-${saved.type}-${saved.property_name || "bien"}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Génération du PDF impossible"); }
  };

  const removeEdl = async (id) => {
    if (!window.confirm("Supprimer cet état des lieux ?")) return;
    try { await api.delete(`/edl/${id}`); toast.success("Supprimé"); load(); refreshExits(); }
    catch { toast.error("Erreur"); }
  };
  const refreshExits = () => api.get("/edl/exits-dashboard").then((r) => setExits(r.data)).catch(() => {});
  const duplicateEdl = async (id, curType) => {
    const target = curType === "sortie" ? "entree" : "sortie";
    if (!window.confirm(`Dupliquer en état des lieux d'${target === "sortie" ? "sortie" : "entrée"} ? (photos et signatures réinitialisées)`)) return;
    try {
      const r = await api.post(`/edl/${id}/duplicate`, { new_type: target });
      toast.success(`Dupliqué en ${target === "sortie" ? "sortie" : "entrée"}`);
      load(); refreshExits();
      setEdl(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Duplication impossible"); }
  };
  const saveAsTemplateFromList = async (id) => {
    try { const r = await api.post(`/edl/${id}/save-as-template`, {}); toast.success(`Modèle « ${r.data.name} » créé`); api.get("/edl/templates").then((t) => setTemplates(t.data || [])); }
    catch { toast.error("Erreur"); }
  };
  const addWorkFromScale = (type) => {
    const w = worksScale.find((x) => x.type === type);
    if (!w) return;
    setEdl((cur) => ({ ...cur, repairs: [...(cur.repairs || []), { label: w.type, category: guessCat(w.type), repair_cost: w.unit_cost, age_years: 0 }] }));
    toast.success("Ligne de travaux ajoutée");
  };
  const downloadComparisonPdf = async () => {
    const id = edl.id;
    try {
      const r = await api.get(`/edl/${id}/comparison-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = `comparatif-alur-${edl.property_name || "bien"}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Comparatif indisponible"); }
  };


  const saveTemplate = async () => {
    const name = window.prompt("Nom du modèle :", `Modèle ${edl.furnished ? "meublé" : ""} ${edl.rooms.length} pièces`.trim());
    if (!name) return;
    try {
      await api.post("/edl/templates", {
        name, furnished: edl.furnished,
        rooms: edl.rooms.map((r) => ({ ...r, photos: [] })),
        inventory: (edl.inventory || []).map((it) => ({ ...it })),
      });
      toast.success("Modèle enregistré");
      api.get("/edl/templates").then((r) => setTemplates(r.data || []));
    } catch { toast.error("Erreur"); }
  };
  const loadTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setEdl((cur) => ({ ...cur, furnished: t.furnished, rooms: (t.rooms || []).map((r) => ({ ...EMPTY_ROOM, ...r, photos: [] })), inventory: (t.inventory || []).map((it) => ({ ...EMPTY_INV, ...it })) }));
    toast.success(`Modèle « ${t.name} » chargé`);
  };

  // ---------- QR signature à distance ----------
  const shareSign = async (party) => {
    const saved = await save(true);
    const id = saved?.id || edl.id;
    if (!id) { toast.error("Enregistrez d'abord l'EDL"); return; }
    try {
      const r = await api.post(`/edl/${id}/share-signature`, { party, origin_url: window.location.origin });
      setQrData({ ...r.data });
    } catch { toast.error("Génération du lien impossible"); }
  };

  // ---------- Vidéo géolocalisée (upload par morceaux) ----------
  const uploadVideo = async (i, file) => {
    if (!file) return;
    setUploadingVideo(i); setVideoProgress(0);
    try {
      const geo = await getGeo();
      const init = (await api.post("/edl/video/init", { filename: file.name || "video.mp4" })).data;
      const CH = 4 * 1024 * 1024;
      const total = Math.max(1, Math.ceil(file.size / CH));
      for (let idx = 0; idx < total; idx++) {
        const blob = file.slice(idx * CH, (idx + 1) * CH);
        const fd = new FormData();
        fd.append("upload_id", init.upload_id); fd.append("index", idx); fd.append("chunk", blob, "chunk");
        await api.post("/edl/video/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setVideoProgress(Math.round(((idx + 1) / total) * 100));
      }
      const done = (await api.post("/edl/video/complete", { upload_id: init.upload_id, ext: init.ext })).data;
      const vid = { url: done.url, lat: geo?.lat ?? null, lng: geo?.lng ?? null, captured_at: new Date().toLocaleString("fr-FR") };
      setEdl((cur) => ({ ...cur, rooms: cur.rooms.map((rm, idx) => (idx === i ? { ...rm, videos: [...(rm.videos || []), vid] } : rm)) }));
      toast.success(geo ? "Vidéo ajoutée (géolocalisée)" : "Vidéo ajoutée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'upload vidéo"); }
    finally { setUploadingVideo(null); setVideoProgress(0); }
  };
  const removeVideo = (i, vi) => setEdl((cur) => ({ ...cur, rooms: cur.rooms.map((rm, idx) => (idx === i ? { ...rm, videos: (rm.videos || []).filter((_, x) => x !== vi) } : rm)) }));

  // ---------- Différences IA -> lignes de réparation ----------
  const guessCat = (text) => {
    const t = (text || "").toLowerCase();
    const map = [["peinture", "Peintures / papiers peints"], ["papier", "Peintures / papiers peints"], ["mur", "Peintures / papiers peints"], ["moquette", "Sols souples / moquette"], ["parquet", "Parquet / carrelage"], ["carrelage", "Parquet / carrelage"], ["faïence", "Revêtements muraux (faïence)"], ["plomberie", "Plomberie / sanitaires"], ["sanitaire", "Plomberie / sanitaires"], ["robinet", "Plomberie / sanitaires"], ["électri", "Électricité / prises"], ["prise", "Électricité / prises"], ["porte", "Menuiseries (portes, fenêtres)"], ["fenêtre", "Menuiseries (portes, fenêtres)"], ["menuiserie", "Menuiseries (portes, fenêtres)"], ["électroménager", "Électroménager"], ["meuble", "Mobilier"], ["mobilier", "Mobilier"]];
    for (const [k, c] of map) { if (t.includes(k) && vetCats.includes(c)) return c; }
    return vetCats.includes("Autre") ? "Autre" : (vetCats[0] || "Autre");
  };
  const addDiffAsRepair = (d) => {
    const label = `${d.room ? d.room + " — " : ""}${d.change || ""}`.slice(0, 120);
    setEdl((cur) => {
      if ((cur.repairs || []).some((r) => r.label === label)) { toast.info("Déjà ajouté"); return cur; }
      return { ...cur, repairs: [...(cur.repairs || []), { label, category: guessCat(`${d.room} ${d.change}`), repair_cost: 0, age_years: 0 }] };
    });
  };
  const diffToRepairs = () => {
    const tenant = (edl.comparison?.differences || []).filter((d) => d.responsibility === "locataire");
    if (!tenant.length) { toast.info("Aucune dégradation à charge du locataire"); return; }
    setEdl((cur) => {
      const existing = new Set((cur.repairs || []).map((r) => r.label));
      const added = tenant.map((d) => ({ label: `${d.room ? d.room + " — " : ""}${d.change || ""}`.slice(0, 120), category: guessCat(`${d.room} ${d.change}`), repair_cost: 0, age_years: 0 })).filter((r) => !existing.has(r.label));
      return { ...cur, repairs: [...(cur.repairs || []), ...added] };
    });
    toast.success(`${tenant.length} dégradation(s) locataire ajoutée(s) aux réparations`);
  };

  // ---------- Relance de signature ----------
  const remindSign = async (party) => {
    const saved = await save(true);
    const id = saved?.id || edl.id;
    if (!id) { toast.error("Enregistrez d'abord l'EDL"); return; }
    const def = party === "referent" ? (edl.referent_email || "") : "";
    const email = window.prompt("E-mail du signataire à relancer :", def);
    if (email === null) return;
    if (!email.trim()) { toast.error("E-mail requis"); return; }
    try {
      const r = await api.post(`/edl/${id}/remind-signature`, { party, email: email.trim(), origin_url: window.location.origin });
      toast.success(`Rappel envoyé à ${r.data.email} (relance n°${r.data.count})`);
      const fresh = (await api.get(`/edl/${id}`)).data;
      setEdl(fresh);
    } catch (e) { toast.error(e?.response?.data?.detail || "Relance impossible"); }
  };

  // ---------- Protocole, signatures, IA, ventilation ----------
  const toggleProtocol = (key) => setEdl((cur) => {
    const done = new Set(cur.protocol_steps || []);
    done.has(key) ? done.delete(key) : done.add(key);
    return { ...cur, protocol_steps: Array.from(done) };
  });

  const submitSignature = async (party, signerName, dataUrl) => {
    const saved = await save(true);
    const id = saved?.id || edl.id;
    if (!id) { toast.error("Enregistrez d'abord l'EDL"); return; }
    try {
      await api.post(`/edl/${id}/sign`, { party, signer_name: signerName, signature_png_b64: dataUrl });
      const fresh = (await api.get(`/edl/${id}`)).data;
      setEdl(fresh);
      setSignParty(null);
      toast.success("Signature enregistrée et scellée");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de signature"); }
  };

  const runCompare = async () => {
    const saved = await save(true);
    const id = saved?.id || edl.id;
    setComparing(true);
    try {
      const r = await api.post(`/edl/${id}/compare`, {});
      setEdl((cur) => ({ ...cur, comparison: r.data }));
      toast.success(`${r.data.differences?.length || 0} différence(s) détectée(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Comparaison impossible"); }
    finally { setComparing(false); }
  };

  const setRepair = (i, patch) => setEdl((cur) => ({ ...cur, repairs: (cur.repairs || []).map((r, idx) => (idx === i ? { ...r, ...patch } : r)) }));
  const addRepair = () => setEdl((cur) => ({ ...cur, repairs: [...(cur.repairs || []), { label: "", category: vetCats[0] || "Autre", repair_cost: 0, age_years: 0 }] }));
  const removeRepair = (i) => setEdl((cur) => ({ ...cur, repairs: (cur.repairs || []).filter((_, idx) => idx !== i) }));
  const clearAllRepairs = async () => {
    if (!window.confirm("Retirer tous les frais et remettre la ventilation à zéro ?")) return;
    setEdl((cur) => ({ ...cur, repairs: [] }));
    const saved = await save(true);
    const id = saved?.id || edl.id;
    setAllocBusy(true);
    try {
      const r = await api.post(`/edl/${id}/cost-allocation`, { items: [] });
      setEdl((cur) => ({ ...cur, repairs: [], cost_allocation: r.data }));
      toast.success("Frais retirés — ventilation remise à zéro");
    } catch (e) { toast.error(e?.response?.data?.detail || "Recalcul impossible"); }
    finally { setAllocBusy(false); }
  };
  const runAllocation = async () => {
    const saved = await save(true);
    const id = saved?.id || edl.id;
    setAllocBusy(true);
    try {
      const items = (edl.repairs || []).map((r) => ({ label: r.label, category: r.category, repair_cost: Number(r.repair_cost) || 0, age_years: Number(r.age_years) || 0 }));
      const r = await api.post(`/edl/${id}/cost-allocation`, { items });
      setEdl((cur) => ({ ...cur, cost_allocation: r.data }));
      toast.success("Ventilation calculée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Calcul impossible"); }
    finally { setAllocBusy(false); }
  };

  // ---------- Décompte de régularisation ----------
  const downloadSettlement = async () => {
    const saved = await save(true);
    const id = saved?.id || edl.id;
    try {
      const r = await api.get(`/edl/${id}/settlement-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `decompte-regularisation-${saved?.property_name || "bien"}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération impossible"); }
  };
  const finalizeEdl = async () => {
    const s = await save(true); const id = s?.id || edl.id;
    try { await api.post(`/edl/${id}/finalize`); setEdl((c) => ({ ...c, status: "completed" })); toast.success("État des lieux terminé et enregistré"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
  };
  const publishEdl = async () => {
    const s = await save(true); const id = s?.id || edl.id;
    try {
      const r = await api.post(`/edl/${id}/publish`, { origin_url: window.location.origin });
      setEdl((c) => ({ ...c, status: "published" }));
      const chs = (r.data.channels || []).map((c) => `${c.email} (${c.channel === "app" ? "App-in" : "e-mail"})`).join(", ");
      toast.success(chs ? `État des lieux publié — notifié : ${chs}` : "État des lieux publié");
    } catch (e) { toast.error(e?.response?.data?.detail || "Publication impossible"); }
  };
  const reopenEdl = async () => {
    if (!window.confirm("Rouvrir l'état des lieux pour modification ? Les signatures éventuelles seront réinitialisées.")) return;
    try { const r = await api.post(`/edl/${edl.id}/reopen`); setEdl((c) => ({ ...c, status: r.data.status, signatures: {}, correction_requested: null })); toast.success("EDL rouvert — vous pouvez le corriger puis le republier"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Réouverture impossible"); }
  };

  const sendSettlement = async () => {
    const email = (edl.tenant_email || "").trim();
    if (!email) { toast.error("Renseignez l'e-mail du locataire dans la section ci-dessus."); return; }
    const saved = await save(true);
    const id = saved?.id || edl.id;
    try {
      const r = await api.post(`/edl/${id}/settlement/send-appin`, { email });
      toast.success(r.data.channel === "app"
        ? `Proposition envoyée dans la messagerie de ${email} (App-in)`
        : `Proposition envoyée par e-mail à ${email} (locataire sans compte)`);
      setEdl((cur) => ({ ...cur, settlement_sent: { email, at: new Date().toISOString(), channel: r.data.channel } }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
  };

  // ---------- Filtres, tri & statut ----------
  const uniq = (key) => [...new Set(list.map((d) => d[key]).filter(Boolean))].sort();
  const signCount = (d) => ["owner", "tenant", "referent"].filter((p) => (d.signatures || {})[p]).length;
  const statusOf = (d) => {
    if (d.status === "completed") return { label: "Signé", cls: "bg-emerald-100 text-emerald-700" };
    const n = signCount(d);
    if (n > 0) return { label: `En cours ${n}/3`, cls: "bg-amber-100 text-amber-700" };
    return { label: "Brouillon", cls: "bg-brand-soft text-brand-green/60" };
  };
  const displayed = list.filter((d) =>
    (!fOwner || d.owner_name === fOwner) &&
    (!fProperty || d.property_name === fProperty) &&
    (!fTenant || d.tenant_name === fTenant) &&
    (!fType || d.type === fType) &&
    (!fSearch || [d.property_name, d.tenant_name, d.owner_name].some((x) => (x || "").toLowerCase().includes(fSearch.toLowerCase()))) &&
    (!fStatus || (fStatus === "completed" ? d.status === "completed"
      : fStatus === "draft" ? (d.status !== "completed" && signCount(d) === 0)
      : (d.status !== "completed" && signCount(d) > 0)))
  ).sort((a, b) => {
    if (sortBy === "date_asc") return (a.edl_date || "").localeCompare(b.edl_date || "");
    if (sortBy === "property") return (a.property_name || "").localeCompare(b.property_name || "");
    if (sortBy === "type") return (a.type || "").localeCompare(b.type || "");
    return (b.edl_date || "").localeCompare(a.edl_date || "");
  });
  const resetFilters = () => { setFOwner(""); setFProperty(""); setFTenant(""); setFType(""); setFStatus(""); setFSearch(""); setSortBy("date_desc"); };
  const settleBalance = (d) => Math.round(((Number(d.deposit) || 0) - (Number(d.cost_allocation?.tenant_total) || 0)) * 100) / 100;
  const csvOf = (rows) => {
    const headers = ["Référence", "Bien", "Type", "Propriétaire", "Locataire", "Date", "Pièces", "Statut", "Signatures", "Part locataire (€)", "Solde décompte (€)"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const body = rows.map((d) => [d.reference || "", d.property_name, d.type === "sortie" ? "Sortie" : "Entrée", d.owner_name, d.tenant_name, d.edl_date, (d.rooms || []).length, statusOf(d).label, `${signCount(d)}/3`, Number(d.cost_allocation?.tenant_total || 0), d.type === "sortie" ? settleBalance(d) : ""].map(esc).join(","));
    return "\uFEFF" + [headers.map(esc).join(","), ...body].join("\r\n");
  };
  const download = (csv, name) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
  };
  const exportCsv = () => {
    if (!displayed.length) { toast.info("Rien à exporter"); return; }
    download(csvOf(displayed), `etats-des-lieux-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`${displayed.length} ligne(s) exportée(s)`);
  };
  const exportSelected = () => {
    const rows = displayed.filter((d) => selected.has(d.id));
    if (!rows.length) return;
    download(csvOf(rows), `etats-des-lieux-selection-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`${rows.length} ligne(s) exportée(s)`);
  };
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelAll = () => setSelected((s) => (s.size === displayed.length ? new Set() : new Set(displayed.map((d) => d.id))));
  const closeExit = async (id) => {
    try { const r = await api.post(`/edl/${id}/close-exit`, {}); toast.success(`Sortie clôturée · ${r.data.files.length} document(s) envoyé(s) à ${r.data.email}`); load(); refreshExits(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Clôture impossible"); }
  };
  const closeSelected = async () => {
    const ids = displayed.filter((d) => selected.has(d.id) && d.type === "sortie").map((d) => d.id);
    if (!ids.length) { toast.info("Sélectionnez des états des lieux de sortie"); return; }
    if (!window.confirm(`Clôturer ${ids.length} sortie(s) et envoyer les documents aux locataires ?`)) return;
    try {
      const r = await api.post("/edl/close-exits", { ids });
      toast.success(`${r.data.closed.length} clôturée(s)` + (r.data.skipped.length ? `, ${r.data.skipped.length} ignorée(s)` : ""));
      if (r.data.skipped.length) toast.info(r.data.skipped.map((s) => s.reason).filter((v, i, a) => a.indexOf(v) === i).join(" · "));
      setSelected(new Set()); load(); refreshExits();
    } catch { toast.error("Erreur"); }
  };

  // ---------- Vue liste ----------
  if (readOnly && !edl) {
    return <div className="flex items-center justify-center py-24 text-brand-green/50" data-testid="edl-readonly-loading"><Loader2 className="w-7 h-7 animate-spin" /></div>;
  }
  if (!edl) {
    if (moduleCfg && !moduleCfg.has_access) {
      const offer = moduleCfg.offer || {};
      return (
        <div className="max-w-2xl mx-auto py-10" data-testid="edl-access-gate">
          <div className="bg-white rounded-3xl border border-brand-soft p-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center mx-auto"><ClipboardCheck className="w-7 h-7" /></div>
            <h2 className="font-display text-2xl font-semibold text-brand-greenDeep mt-4">Module États des lieux</h2>
            {!moduleCfg.enabled ? (
              <p className="text-sm text-brand-green/60 mt-2">Ce module est actuellement désactivé par l'administrateur du site.</p>
            ) : (
              <>
                <p className="text-sm text-brand-green/60 mt-2">
                  Ce module n'est pas inclus dans votre offre. Souscrivez à l'offre indépendante « {offer.name || "État des lieux"} » pour en profiter{offer.price_monthly ? `, à partir de ${offer.price_monthly}€ / mois` : ""}.
                </p>
                {offer.enabled && (offer.services || []).length > 0 && (
                  <ul className="text-left text-sm text-brand-green/70 mt-5 space-y-1.5 inline-block">
                    {(offer.services || []).map((s, i) => (
                      <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-brand-greenDeep mt-0.5 shrink-0" /> {s}</li>
                    ))}
                  </ul>
                )}
                {offer.enabled && (
                  <div className="mt-6">
                    {offer.price_yearly > 0 && (
                      <div className="inline-flex items-center rounded-full border border-brand-soft p-1 mb-4" data-testid="edl-cycle-toggle">
                        <button onClick={() => setSubCycle("monthly")} className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${subCycle === "monthly" ? "bg-brand-greenDeep text-white" : "text-brand-greenDeep"}`} data-testid="edl-cycle-monthly">Mensuel</button>
                        <button onClick={() => setSubCycle("yearly")} className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${subCycle === "yearly" ? "bg-brand-greenDeep text-white" : "text-brand-greenDeep"}`} data-testid="edl-cycle-yearly">Annuel</button>
                      </div>
                    )}
                    <div>
                      <button onClick={subscribeEdl} disabled={subscribing} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold transition-colors disabled:opacity-60" data-testid="edl-subscribe-btn">
                        {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        {(() => { const amt = subCycle === "yearly" ? offer.price_yearly : offer.price_monthly; return amt > 0 ? `S'abonner — ${amt}€ / ${subCycle === "yearly" ? "an" : "mois"}` : "Activer l'offre (gratuit)"; })()}
                      </button>
                    </div>
                    <p className="text-[11px] text-brand-green/40 mt-2">{(subCycle === "yearly" ? offer.price_yearly : offer.price_monthly) > 0 ? "Paiement sécurisé par Stripe · abonnement résiliable." : "Activation immédiate."}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-6" data-testid="edl-page">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><ClipboardCheck className="w-5 h-5" /></div>
            <div>
              <h2 className="font-display text-2xl font-semibold text-brand-greenDeep">États des lieux</h2>
              <p className="text-sm text-brand-green/55">Constats d'entrée et de sortie digitaux, photos géolocalisées, inventaire</p>
            </div>
          </div>
          <button onClick={newEdl} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold transition-colors" data-testid="new-edl-btn">
            <Plus className="w-4 h-4" /> Nouvel état des lieux
          </button>
          {exits && (exits.exits || []).length > 0 && (
            <button onClick={() => setShowExits((v) => !v)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-sm font-semibold transition-colors" data-testid="exits-toggle-btn">
              <DoorClosed className="w-4 h-4" /> Sorties en cours{exits.pending_count > 0 ? ` (${exits.pending_count})` : ""}
            </button>
          )}
        </div>

        {showExits && exits && (
          <div className="bg-white rounded-3xl border border-brand-soft overflow-x-auto" data-testid="exits-dashboard">
            <div className="px-5 pt-5"><h3 className="font-display text-lg font-semibold text-brand-greenDeep">Suivi des sorties de locataires</h3>
              <p className="text-sm text-brand-green/55 mb-3">Avancement de chaque départ : signatures, comparatif, ventilation, décompte envoyé.</p></div>
            <table className="w-full text-sm">
              <thead className="bg-brand-softer text-brand-green/60"><tr>
                <th className="text-left px-5 py-2 font-semibold">Bien</th>
                <th className="text-left px-5 py-2 font-semibold">Locataire</th>
                <th className="text-left px-5 py-2 font-semibold">Signatures</th>
                <th className="text-left px-5 py-2 font-semibold">Comparatif</th>
                <th className="text-left px-5 py-2 font-semibold">Ventilation</th>
                <th className="text-left px-5 py-2 font-semibold">Décompte</th>
                <th className="px-5 py-2"></th>
              </tr></thead>
              <tbody>
                {exits.exits.map((e) => {
                  const Chip = ({ ok, label }) => <span className={`text-xs font-semibold ${ok ? "text-emerald-600" : "text-brand-green/40"}`}>{ok ? "✓" : "—"} {label}</span>;
                  return (
                    <tr key={e.id} className="border-t border-brand-soft" data-testid={`exit-row-${e.id}`}>
                      <td className="px-5 py-2 text-brand-greenDeep font-medium">{e.property_name}</td>
                      <td className="px-5 py-2 text-brand-green/70">{e.tenant_name || "—"}</td>
                      <td className="px-5 py-2"><span className={`font-semibold ${e.signatures_done === 3 ? "text-emerald-600" : "text-amber-600"}`}>{e.signatures_done}/3</span>{e.missing_signatures.length > 0 && <span className="text-xs text-brand-green/45"> ({e.missing_signatures.join(", ")})</span>}</td>
                      <td className="px-5 py-2"><Chip ok={e.compared} /></td>
                      <td className="px-5 py-2"><Chip ok={e.allocated} /></td>
                      <td className="px-5 py-2"><Chip ok={e.settlement_sent} /></td>
                      <td className="px-5 py-2 text-right">
                        {e.closed ? (
                          <span className="text-xs font-semibold text-emerald-600">✓ Clôturé</span>
                        ) : e.signatures_done === 3 ? (
                          <button onClick={() => closeExit(e.id)} className="px-3 py-1.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-xs font-semibold" data-testid={`close-exit-${e.id}`}>Clôturer</button>
                        ) : (
                          <button onClick={() => openEdl(e.id)} className="px-4 py-1.5 rounded-full bg-brand-soft hover:bg-brand-mint text-brand-greenDeep text-xs font-semibold" data-testid={`exit-open-${e.id}`}>Finaliser</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24 text-brand-green/50"><Loader2 className="w-7 h-7 animate-spin" /></div>
        ) : list.length === 0 ? (
          <div className="text-center py-24 text-brand-green/50" data-testid="no-edl">Aucun état des lieux. Cliquez sur « Nouvel état des lieux » pour commencer.</div>
        ) : (
          <>
            {/* Recherche + export */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="w-4 h-4 text-brand-green/40 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={fSearch} onChange={(e) => setFSearch(e.target.value)} placeholder="Rechercher un bien, un locataire, un propriétaire…" className={`${inp} pl-9`} data-testid="edl-search" />
                {fSearch && <button onClick={() => setFSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-green/40 hover:text-brand-greenDeep"><X className="w-4 h-4" /></button>}
              </div>
              <button onClick={exportCsv} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold" data-testid="edl-export-csv"><Download className="w-4 h-4" /> Exporter CSV</button>
            </div>

            {selected.size > 0 && (
              <div className="flex flex-wrap items-center gap-3 bg-brand-lime/20 border border-brand-lime rounded-2xl px-4 py-2" data-testid="selection-bar">
                <span className="text-sm font-semibold text-brand-greenDeep">{selected.size} sélectionné(s)</span>
                <button onClick={exportSelected} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-brand-soft bg-white hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold" data-testid="export-selected-btn"><Download className="w-4 h-4" /> Exporter la sélection</button>
                <button onClick={closeSelected} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold" data-testid="close-selected-btn"><CheckCircle2 className="w-4 h-4" /> Clôturer les sorties</button>
                <button onClick={() => setSelected(new Set())} className="text-sm text-brand-green/60 hover:text-brand-greenDeep">Annuler</button>
              </div>
            )}

            {/* Barre de filtres */}
            <div className="bg-white rounded-3xl border border-brand-soft p-4 flex flex-wrap items-end gap-3" data-testid="edl-filters">
              <label className="block"><span className="text-xs text-brand-green/55 mb-1 block">Propriétaire</span>
                <select value={fOwner} onChange={(e) => setFOwner(e.target.value)} className={`${inp} w-44`} data-testid="filter-owner">
                  <option value="">Tous</option>{uniq("owner_name").map((v) => <option key={v} value={v}>{v}</option>)}
                </select></label>
              <label className="block"><span className="text-xs text-brand-green/55 mb-1 block">Bien</span>
                <select value={fProperty} onChange={(e) => setFProperty(e.target.value)} className={`${inp} w-44`} data-testid="filter-property">
                  <option value="">Tous</option>{uniq("property_name").map((v) => <option key={v} value={v}>{v}</option>)}
                </select></label>
              <label className="block"><span className="text-xs text-brand-green/55 mb-1 block">Locataire</span>
                <select value={fTenant} onChange={(e) => setFTenant(e.target.value)} className={`${inp} w-40`} data-testid="filter-tenant">
                  <option value="">Tous</option>{uniq("tenant_name").map((v) => <option key={v} value={v}>{v}</option>)}
                </select></label>
              <label className="block"><span className="text-xs text-brand-green/55 mb-1 block">Type</span>
                <select value={fType} onChange={(e) => setFType(e.target.value)} className={`${inp} w-36`} data-testid="filter-type">
                  <option value="">Tous</option><option value="entree">Entrée</option><option value="sortie">Sortie</option>
                </select></label>
              <label className="block"><span className="text-xs text-brand-green/55 mb-1 block">Statut</span>
                <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={`${inp} w-36`} data-testid="filter-status">
                  <option value="">Tous</option><option value="draft">Brouillon</option><option value="in_progress">En cours</option><option value="completed">Signé</option>
                </select></label>
              <label className="block"><span className="text-xs text-brand-green/55 mb-1 block">Trier par</span>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${inp} w-44`} data-testid="filter-sort">
                  <option value="date_desc">Date (récent → ancien)</option><option value="date_asc">Date (ancien → récent)</option>
                  <option value="property">Bien (A→Z)</option><option value="type">Type</option>
                </select></label>
              <button onClick={resetFilters} className="px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold" data-testid="filter-reset">Réinitialiser</button>
              <span className="text-xs text-brand-green/45 ml-auto self-center" data-testid="edl-count">{displayed.length} état(s) des lieux</span>
            </div>

            {/* Tableau */}
            <div className="bg-white rounded-3xl border border-brand-soft overflow-x-auto" data-testid="edl-table">
              <table className="w-full text-sm">
                <thead className="bg-brand-softer text-brand-green/60"><tr>
                  <th className="px-4 py-3"><input type="checkbox" checked={displayed.length > 0 && selected.size === displayed.length} onChange={toggleSelAll} className="w-4 h-4 accent-brand-greenDeep" data-testid="select-all" /></th>
                  <th className="text-left px-5 py-3 font-semibold">Référence</th>
                  <th className="text-left px-5 py-3 font-semibold">Bien</th>
                  <th className="text-left px-5 py-3 font-semibold">Type</th>
                  <th className="text-left px-5 py-3 font-semibold">Propriétaire</th>
                  <th className="text-left px-5 py-3 font-semibold">Locataire</th>
                  <th className="text-left px-5 py-3 font-semibold">Date</th>
                  <th className="text-left px-5 py-3 font-semibold">Statut</th>
                  <th className="px-5 py-3"></th>
                </tr></thead>
                <tbody>
                  {displayed.map((d) => {
                    const st = statusOf(d);
                    return (
                      <tr key={d.id} className="border-t border-brand-soft hover:bg-brand-softer/40" data-testid="edl-row">
                        <td className="px-4 py-3"><input type="checkbox" checked={selected.has(d.id)} onChange={() => toggleSel(d.id)} className="w-4 h-4 accent-brand-greenDeep" data-testid={`select-${d.id}`} /></td>
                        <td className="px-5 py-3 whitespace-nowrap"><span className="font-mono text-xs font-semibold text-brand-greenDeep bg-brand-softer px-2 py-1 rounded-md" data-testid={`edl-ref-${d.id}`}>{d.reference || "—"}</span></td>
                        <td className="px-5 py-3 font-medium text-brand-greenDeep">
                          <span className="flex items-center gap-2"><Home className="w-4 h-4 text-brand-green/40" />{d.property_name || "Bien"}{d.furnished && <span className="text-[10px] font-semibold text-brand-green/50 inline-flex items-center gap-0.5"><Package className="w-3 h-3" />Meublé</span>}</span>
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${d.type === "sortie" ? "bg-amber-100 text-amber-700" : "bg-brand-lime/30 text-brand-greenDeep"}`}>
                            {d.type === "sortie" ? <DoorClosed className="w-3 h-3" /> : <DoorOpen className="w-3 h-3" />}{d.type === "sortie" ? "Sortie" : "Entrée"}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-brand-green/70">{d.owner_name || "—"}</td>
                        <td className="px-5 py-3 text-brand-green/70">{d.tenant_name || "—"}</td>
                        <td className="px-5 py-3 text-brand-green/70">{d.edl_date}</td>
                        <td className="px-5 py-3"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${st.cls}`} data-testid={`status-${d.id}`}>{st.label}</span></td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <button onClick={() => openEdl(d.id)} className="px-3 py-1.5 rounded-full bg-brand-soft hover:bg-brand-mint text-brand-greenDeep text-xs font-semibold" data-testid={`open-edl-${d.id}`}>Ouvrir</button>
                            <button onClick={() => duplicateEdl(d.id, d.type)} className="p-1.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep" title={`Dupliquer en ${d.type === "sortie" ? "entrée" : "sortie"}`} data-testid={`duplicate-edl-${d.id}`}><Copy className="w-4 h-4" /></button>
                            <button onClick={() => saveAsTemplateFromList(d.id)} className="p-1.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep" title="Créer un modèle" data-testid={`template-from-edl-${d.id}`}><LayoutTemplate className="w-4 h-4" /></button>
                            <button onClick={() => removeEdl(d.id)} className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-500" title="Supprimer" data-testid={`delete-edl-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {displayed.length === 0 && (
                    <tr><td colSpan={9} className="px-5 py-8 text-center text-brand-green/50" data-testid="edl-no-match">Aucun état des lieux ne correspond aux filtres.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  }

  // ---------- Éditeur / wizard ----------
  const SIGN_SEQ = ["referent", "tenant", "owner"];
  const SIGN_LBL = { referent: "du référent", tenant: "du locataire", owner: "du propriétaire" };
  const nextSigner = edl ? SIGN_SEQ.find((p) => !(edl.signatures || {})[p]) : null;
  const locked = readOnly || edl.status === "completed" || edl.status === "published" || ["owner", "tenant", "referent"].some((p) => (edl.signatures || {})[p]);
  const closeView = () => { openedParam.current = false; setEdl(null); if (onCloseView) onCloseView(); };
  return (
    <div className="space-y-6" data-testid="edl-editor">
      {readOnly && (
        <div className="flex items-center justify-between gap-3 bg-brand-lime/20 border border-brand-lime rounded-2xl px-4 py-2.5" data-testid="edl-readonly-bar">
          <span className="text-sm font-semibold text-brand-greenDeep flex items-center gap-2"><Search className="w-4 h-4" /> Lecture seule — visualisation de l'état des lieux</span>
          <button onClick={closeView} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold" data-testid="edl-view-close"><X className="w-4 h-4" /> Fermer</button>
        </div>
      )}
      <div className={readOnly ? "space-y-6 pointer-events-none select-none" : "contents"}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => setEdl(null)} className="inline-flex items-center gap-1.5 text-sm text-brand-green/70 hover:text-brand-greenDeep" data-testid="edl-back-btn"><ChevronLeft className="w-4 h-4" /> Retour</button>
        {!readOnly && (
        <div className="flex items-center gap-2">
          <button onClick={downloadPdf} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-sm font-semibold" data-testid="edl-pdf-btn"><FileText className="w-4 h-4" /> PDF</button>
          <button onClick={() => save()} disabled={busy} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="edl-save-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
          </button>
        </div>
        )}
      </div>

      <fieldset disabled={locked} className="space-y-6 border-0 p-0 m-0 min-w-0">
      <datalist id="edl-clients-owners">{(clients.owners || []).map((c) => <option key={c.id} value={c.name} />)}</datalist>
      <datalist id="edl-clients-tenants">{(clients.tenants || []).map((c) => <option key={c.id} value={c.name} />)}</datalist>
      <datalist id="edl-clients-properties">{(clients.properties || []).map((c) => <option key={c.id} value={c.name} />)}</datalist>
      {/* En-tête EDL */}
      <div className="bg-white rounded-3xl border border-brand-soft p-6 space-y-4">
        <div className="grid md:grid-cols-4 gap-4">
          <Field label="Bien concerné">
            {moduleCfg?.allow_integrated && !manualMode ? (
              <select value={edl.property_id} onChange={(e) => onPropChange(e.target.value)} className={inp} data-testid="edl-prop-select">
                <option value="">— Sélectionner —</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            ) : (
              <input value={edl.property_name || ""} onChange={(e) => setEdl({ ...edl, property_id: "", property_name: e.target.value })} className={inp} placeholder="Nom / adresse du bien" list="edl-clients-properties" data-testid="edl-prop-manual" />
            )}
            {moduleCfg?.allow_integrated && moduleCfg?.allow_independent && (
              <button type="button" onClick={() => setManualMode((v) => !v)} className="mt-1.5 text-[11px] font-semibold text-brand-green/60 hover:text-brand-greenDeep underline" data-testid="edl-mode-toggle">
                {manualMode ? "↩ Lier un bien existant" : "✎ Saisie manuelle (bien hors plateforme)"}
              </button>
            )}
          </Field>
          <Field label="Type d'état des lieux">
            <select value={edl.type} onChange={(e) => setEdl({ ...edl, type: e.target.value })} className={inp} data-testid="edl-type-select">
              <option value="entree">Entrée</option>
              <option value="sortie">Sortie</option>
            </select>
          </Field>
          <Field label="Date"><input type="date" value={edl.edl_date} onChange={(e) => setEdl({ ...edl, edl_date: e.target.value })} className={inp} /></Field>
          <label className="flex items-center gap-2 mt-6">
            <input type="checkbox" checked={edl.furnished} onChange={(e) => setEdl({ ...edl, furnished: e.target.checked })} className="w-4 h-4 accent-brand-greenDeep" data-testid="edl-furnished-toggle" />
            <span className="text-sm text-brand-greenDeep font-medium">Meublé / saisonnier (inventaire)</span>
          </label>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <Field label="Bailleur"><input value={edl.owner_name} onChange={(e) => setEdl({ ...edl, owner_name: e.target.value })} className={inp} list="edl-clients-owners" data-testid="edl-owner-input" /></Field>
          <Field label="Bailleur (e-mail)"><input value={edl.owner_email || ""} onChange={(e) => setEdl({ ...edl, owner_email: e.target.value })} className={inp} placeholder="Pour l'accès permanent à l'EDL" data-testid="edl-owner-email" /></Field>
          <Field label="Locataire"><input value={edl.tenant_name} onChange={(e) => setEdl({ ...edl, tenant_name: e.target.value })} className={inp} list="edl-clients-tenants" data-testid="edl-tenant-input" /></Field>
          <Field label="Référent (nom)"><input value={edl.referent_name} onChange={(e) => setEdl({ ...edl, referent_name: e.target.value })} className={inp} placeholder="Optionnel" data-testid="edl-referent-name" /></Field>
          <Field label="Référent (e-mail)"><input value={edl.referent_email} onChange={(e) => setEdl({ ...edl, referent_email: e.target.value })} className={inp} placeholder="Optionnel" data-testid="edl-referent-email" /></Field>
        </div>
      </div>

      {/* Pièces */}
      <div className="space-y-4">
        {["owner", "tenant", "referent"].some((p) => (edl.signatures || {})[p]) && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-2 text-sm text-amber-800" data-testid="signed-lock-banner">
            🔒 Cet état des lieux a été signé par au moins une partie : il n'est plus modifiable.
          </div>
        )}
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-brand-greenDeep">Pièces & surfaces</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setEdl((cur) => ({ ...cur, rooms: [...cur.rooms].map((r, i) => ({ ...r, sequence: r.sequence || i + 1 })).sort((a, b) => (a.sequence || 0) - (b.sequence || 0)) }))} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold" data-testid="sort-rooms-seq-btn"><ListChecks className="w-4 h-4" /> Trier par séquence</button>
            <button onClick={addRoom} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-soft hover:bg-brand-mint text-brand-greenDeep text-sm font-semibold" data-testid="add-room-btn"><Plus className="w-4 h-4" /> Ajouter une pièce</button>
          </div>
        </div>
        <p className="text-xs text-brand-green/50 -mt-2">Attribuez un numéro de séquence (1, 2, 3…) pour définir l'ordre de réalisation ; remplissez chaque pièce (état, photos/vidéos) puis enregistrez avant de passer à la suivante. Modifiable tant qu'aucune partie n'a signé.</p>
        {edl.rooms.map((r, i) => (
          <div key={i} className="bg-white rounded-2xl border border-brand-soft p-5" data-testid={`room-card-${i}`}>
            <div className="flex items-center gap-3">
              <input type="number" min="1" value={r.sequence || ""} onChange={(e) => setRoom(i, { sequence: Number(e.target.value) || 0 })} className={`${inp} w-16 text-center font-semibold`} placeholder="N°" title="Numéro de séquence" data-testid={`room-seq-${i}`} />
              <input value={r.name} onChange={(e) => setRoom(i, { name: e.target.value })} className={`${inp} font-semibold`} data-testid={`room-name-${i}`} />
              <select value={r.condition} onChange={(e) => setRoom(i, { condition: e.target.value })} className={`${inp} w-44`} data-testid={`room-condition-${i}`}>
                <option value="">État…</option>
                {conditions.map((c) => <option key={c}>{c}</option>)}
              </select>
              <button onClick={() => removeRoom(i)} className="p-2 rounded-full hover:bg-red-50 text-red-500 shrink-0" title="Supprimer la pièce"><Trash2 className="w-4 h-4" /></button>
              <button onClick={() => save()} disabled={busy} className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-xs font-semibold disabled:opacity-60" title="Enregistrer et passer à la pièce suivante" data-testid={`save-room-${i}`}><Save className="w-3.5 h-3.5" />Enregistrer</button>
            </div>
            <div className="grid md:grid-cols-3 gap-3 mt-3">
              <Field label="Sols"><input value={r.sols} onChange={(e) => setRoom(i, { sols: e.target.value })} className={inp} /></Field>
              <Field label="Murs"><input value={r.murs} onChange={(e) => setRoom(i, { murs: e.target.value })} className={inp} /></Field>
              <Field label="Plafond"><input value={r.plafond} onChange={(e) => setRoom(i, { plafond: e.target.value })} className={inp} /></Field>
            </div>
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <Field label="Équipements"><input value={r.equipment} onChange={(e) => setRoom(i, { equipment: e.target.value })} className={inp} placeholder="Radiateur, prises, placards…" /></Field>
              <Field label="Observations"><input value={r.observations} onChange={(e) => setRoom(i, { observations: e.target.value })} className={inp} /></Field>
            </div>
            {/* Photos */}
            <div className="mt-3">
              <div className="flex flex-wrap gap-3">
                {(r.photos || []).map((ph, pi) => (
                  <div key={pi} className="relative w-28 rounded-xl overflow-hidden border border-brand-soft group">
                    <img src={fileUrl(ph.url)} alt="" className="w-28 h-20 object-cover" />
                    <div className="px-1.5 py-1 text-[9px] text-brand-green/70 leading-tight">
                      {ph.lat != null ? <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{ph.lat.toFixed(4)}, {ph.lng.toFixed(4)}</span> : <span className="text-brand-green/40">Sans géoloc.</span>}
                      <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{ph.captured_at}</span>
                    </div>
                    <button onClick={() => removePhoto(i, pi)} className="absolute top-1 right-1 p-1 rounded-full bg-black/55 text-white opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <label className="w-28 h-[92px] rounded-xl border-2 border-dashed border-brand-soft hover:border-brand-lime flex flex-col items-center justify-center gap-1 text-brand-green/50 text-xs cursor-pointer" data-testid={`add-photo-${i}`}>
                  {uploadingRoom === i ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Camera className="w-5 h-5" /> Photo</>}
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { capturePhoto(i, e.target.files?.[0]); e.target.value = ""; }} data-testid={`room-photo-input-${i}`} />
                </label>
              </div>
              {/* Vidéos géolocalisées */}
              <div className="flex flex-wrap gap-3 mt-3">
                {(r.videos || []).map((v, vi) => (
                  <div key={vi} className="relative w-40 rounded-xl overflow-hidden border border-brand-soft group">
                    <video src={fileUrl(v.url)} className="w-40 h-24 object-cover bg-black" controls preload="metadata" />
                    <div className="px-1.5 py-1 text-[9px] text-brand-green/70 leading-tight">
                      {v.lat != null ? <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{v.lat.toFixed(4)}, {v.lng.toFixed(4)}</span> : <span className="text-brand-green/40">Sans géoloc.</span>}
                      <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{v.captured_at}</span>
                    </div>
                    <button onClick={() => removeVideo(i, vi)} className="absolute top-1 right-1 p-1 rounded-full bg-black/55 text-white opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <label className="w-40 h-[92px] rounded-xl border-2 border-dashed border-brand-soft hover:border-brand-lime flex flex-col items-center justify-center gap-1 text-brand-green/50 text-xs cursor-pointer" data-testid={`add-video-${i}`}>
                  {uploadingVideo === i ? <><Loader2 className="w-5 h-5 animate-spin" /><span>{videoProgress}%</span></> : <><Video className="w-5 h-5" /> Vidéo</>}
                  <input type="file" accept="video/*" capture="environment" className="hidden" onChange={(e) => { uploadVideo(i, e.target.files?.[0]); e.target.value = ""; }} data-testid={`room-video-input-${i}`} />
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Compteurs & clés */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-brand-soft p-5">
          <h4 className="font-semibold text-brand-greenDeep flex items-center gap-2 mb-3"><Gauge className="w-4 h-4" /> Relevé des compteurs</h4>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Électricité (kWh)"><input value={edl.meters.electricite} onChange={(e) => setEdl({ ...edl, meters: { ...edl.meters, electricite: e.target.value } })} className={inp} data-testid="meter-elec" /></Field>
            <Field label="Gaz (m³)"><input value={edl.meters.gaz} onChange={(e) => setEdl({ ...edl, meters: { ...edl.meters, gaz: e.target.value } })} className={inp} /></Field>
            <Field label="Eau froide (m³)"><input value={edl.meters.eau_froide} onChange={(e) => setEdl({ ...edl, meters: { ...edl.meters, eau_froide: e.target.value } })} className={inp} /></Field>
            <Field label="Eau chaude (m³)"><input value={edl.meters.eau_chaude} onChange={(e) => setEdl({ ...edl, meters: { ...edl.meters, eau_chaude: e.target.value } })} className={inp} /></Field>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-brand-soft p-5">
          <h4 className="font-semibold text-brand-greenDeep flex items-center gap-2 mb-3"><KeyRound className="w-4 h-4" /> Clés remises</h4>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Clés"><input value={edl.keys.cles} onChange={(e) => setEdl({ ...edl, keys: { ...edl.keys, cles: e.target.value } })} className={inp} data-testid="key-cles" /></Field>
            <Field label="Badges / télécom."><input value={edl.keys.badges} onChange={(e) => setEdl({ ...edl, keys: { ...edl.keys, badges: e.target.value } })} className={inp} /></Field>
            <Field label="Boîte aux lettres"><input value={edl.keys.boite_lettres} onChange={(e) => setEdl({ ...edl, keys: { ...edl.keys, boite_lettres: e.target.value } })} className={inp} /></Field>
          </div>
        </div>
      </div>

      {/* Inventaire (meublé) */}
      {edl.furnished && (
        <div className="bg-white rounded-2xl border border-brand-soft p-5" data-testid="inventory-section">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-brand-greenDeep flex items-center gap-2"><Package className="w-4 h-4" /> Inventaire du mobilier</h4>
            <button onClick={addInv} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-soft hover:bg-brand-mint text-brand-greenDeep text-sm font-semibold" data-testid="add-inventory-btn"><Plus className="w-4 h-4" /> Ajouter</button>
          </div>
          {(edl.inventory || []).length === 0 ? (
            <p className="text-sm text-brand-green/50">Aucun élément. Ajoutez le mobilier et les équipements fournis.</p>
          ) : (
            <div className="space-y-2">
              {edl.inventory.map((it, i) => (
                <div key={i} className="grid md:grid-cols-12 gap-2 items-center" data-testid={`inv-row-${i}`}>
                  <input value={it.item} onChange={(e) => setInv(i, { item: e.target.value })} placeholder="Élément" className={`${inp} md:col-span-3`} />
                  <select value={it.room} onChange={(e) => setInv(i, { room: e.target.value })} className={`${inp} md:col-span-3`}>
                    <option value="">Pièce…</option>
                    {edl.rooms.map((r, ri) => <option key={ri} value={r.name}>{r.name}</option>)}
                  </select>
                  <input type="number" min="1" value={it.quantity} onChange={(e) => setInv(i, { quantity: Number(e.target.value) || 1 })} className={`${inp} md:col-span-1`} />
                  <select value={it.condition} onChange={(e) => setInv(i, { condition: e.target.value })} className={`${inp} md:col-span-2`}>
                    <option value="">État…</option>
                    {conditions.map((c) => <option key={c}>{c}</option>)}
                  </select>
                  <input value={it.observations} onChange={(e) => setInv(i, { observations: e.target.value })} placeholder="Obs." className={`${inp} md:col-span-2`} />
                  <button onClick={() => removeInv(i)} className="p-2 rounded-full hover:bg-red-50 text-red-500 md:col-span-1 justify-self-start"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-brand-soft p-5">
        <Field label="Observations générales"><textarea rows={3} value={edl.general_observations} onChange={(e) => setEdl({ ...edl, general_observations: e.target.value })} className={inp} /></Field>
      </div>

      {/* Protocole guidé du référent */}
      <div className="bg-white rounded-2xl border border-brand-soft p-5" data-testid="protocol-section">
        <h4 className="font-semibold text-brand-greenDeep flex items-center gap-2 mb-3"><ListChecks className="w-4 h-4" /> Protocole étape par étape</h4>
        <div className="space-y-2">
          {protocolSteps.map((s, i) => {
            const done = (edl.protocol_steps || []).includes(s.key);
            return (
              <label key={s.key} className="flex items-center gap-3 cursor-pointer" data-testid={`protocol-step-${i}`}>
                <input type="checkbox" checked={done} onChange={() => toggleProtocol(s.key)} className="w-4 h-4 accent-brand-greenDeep" />
                <span className={`text-sm ${done ? "line-through text-brand-green/40" : "text-brand-greenDeep"}`}>{i + 1}. {s.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      </fieldset>

      {/* Comparaison IA & ventilation — état des lieux de SORTIE */}
      {edl.type === "sortie" && (
        <>
          <div className="bg-white rounded-2xl border border-brand-soft p-5" data-testid="compare-section">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-brand-greenDeep flex items-center gap-2"><Sparkles className="w-4 h-4 text-brand-limeDark" /> Comparaison IA entrée / sortie</h4>
              <button onClick={runCompare} disabled={comparing} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="compare-btn">
                {comparing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Comparer avec l'entrée
              </button>
            </div>
            <p className="text-xs text-brand-green/50 mb-3">L'IA compare cet EDL de sortie avec l'EDL d'entrée du même bien et liste les différences.</p>
            {edl.comparison && (
              <div data-testid="compare-result">
                <button onClick={downloadComparisonPdf} className="mb-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold" data-testid="comparison-pdf-btn"><FileText className="w-4 h-4" /> Éditer le comparatif (PDF loi ALUR)</button>
                {edl.comparison.summary && <p className="text-sm text-brand-green/75 mb-3 italic">{edl.comparison.summary}</p>}
                {(edl.comparison.differences || []).some((d) => d.responsibility === "locataire") && (
                  <button onClick={diffToRepairs} className="mb-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-sm font-semibold" data-testid="diff-to-repairs-btn">
                    <Scale className="w-4 h-4" /> Convertir les dégradations locataire en réparations
                  </button>
                )}
                {(edl.comparison.differences || []).length === 0 ? (
                  <p className="text-sm text-emerald-600">Aucune différence significative détectée.</p>
                ) : (
                  <div className="space-y-2">
                    {edl.comparison.differences.map((d, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-brand-softer" data-testid={`diff-${i}`}>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full mt-0.5 ${d.severity === "grave" ? "bg-red-100 text-red-700" : d.severity === "moyenne" ? "bg-amber-100 text-amber-700" : "bg-brand-lime/40 text-brand-greenDeep"}`}>{d.severity?.toUpperCase()}</span>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-brand-greenDeep">{d.room} — {d.change}</p>
                          <p className="text-xs text-brand-green/55">Responsabilité probable : {d.responsibility}</p>
                        </div>
                        {d.responsibility === "locataire" && (
                          <button onClick={() => addDiffAsRepair(d)} className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-brand-soft hover:bg-brand-lime text-brand-greenDeep text-xs font-semibold" title="Ajouter aux réparations" data-testid={`diff-add-${i}`}><Plus className="w-3 h-3" />Réparation</button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-brand-soft p-5" data-testid="alloc-section">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-brand-greenDeep flex items-center gap-2"><Scale className="w-4 h-4" /> Ventilation des réparations (grille de vétusté)</h4>
              <div className="flex items-center gap-2">
                {worksScale.length > 0 && (
                  <select onChange={(e) => { if (e.target.value) addWorkFromScale(e.target.value); e.target.value = ""; }} className={`${inp} w-auto text-xs`} data-testid="works-scale-select" defaultValue="">
                    <option value="" disabled>Chiffrer un poste de travaux…</option>
                    {worksScale.map((w) => <option key={w.type} value={w.type}>{`${w.type} — ${w.unit_cost} €`}</option>)}
                  </select>
                )}
                <button onClick={addRepair} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-soft hover:bg-brand-mint text-brand-greenDeep text-sm font-semibold" data-testid="add-repair-btn"><Plus className="w-4 h-4" /> Ajouter</button>
              </div>
            </div>
            <p className="text-xs text-brand-green/50 mb-3">Selon la grille de vétusté (décret n° 2016-382) : la part du locataire diminue avec l'âge de l'élément.</p>
            {(edl.repairs || []).length === 0 ? (
              <p className="text-sm text-brand-green/50">Ajoutez les réparations constatées pour calculer la part locataire / propriétaire.</p>
            ) : (
              <div className="space-y-2">
                {edl.repairs.map((r, i) => (
                  <div key={i} className="grid md:grid-cols-12 gap-2 items-center" data-testid={`repair-row-${i}`}>
                    <input value={r.label} onChange={(e) => setRepair(i, { label: e.target.value })} placeholder="Réparation" className={`${inp} md:col-span-4`} />
                    <select value={r.category} onChange={(e) => setRepair(i, { category: e.target.value })} className={`${inp} md:col-span-3`}>
                      {vetCats.map((c) => <option key={c}>{c}</option>)}
                    </select>
                    <input type="number" value={r.repair_cost} onChange={(e) => setRepair(i, { repair_cost: e.target.value })} placeholder="Coût €" className={`${inp} md:col-span-2`} />
                    <input type="number" step="0.5" value={r.age_years} onChange={(e) => setRepair(i, { age_years: e.target.value })} placeholder="Âge (ans)" className={`${inp} md:col-span-2`} />
                    <button onClick={() => removeRepair(i)} className="p-2 rounded-full hover:bg-red-50 text-red-500 md:col-span-1 justify-self-start" title="Retirer ce frais" data-testid={`remove-repair-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button onClick={runAllocation} disabled={allocBusy} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="run-alloc-btn">
                {allocBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />} {edl.cost_allocation ? "Recalculer la ventilation" : "Calculer la ventilation"}
              </button>
              {((edl.repairs || []).length > 0 || edl.cost_allocation) && (
                <button onClick={clearAllRepairs} disabled={allocBusy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-red-200 text-red-500 hover:bg-red-50 text-sm font-semibold disabled:opacity-60" data-testid="clear-repairs-btn"><Trash2 className="w-4 h-4" /> Retirer tous les frais</button>
              )}
            </div>
            {edl.cost_allocation && (
              <div className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="alloc-result">
                <div className="rounded-xl bg-brand-softer p-3"><p className="text-xs text-brand-green/50">Total réparations</p><p className="font-semibold text-brand-greenDeep">{Number(edl.cost_allocation.total_cost).toLocaleString("fr-FR")} €</p></div>
                <div className="rounded-xl bg-red-50 p-3"><p className="text-xs text-red-500/70">Part locataire</p><p className="font-semibold text-red-600" data-testid="alloc-tenant">{Number(edl.cost_allocation.tenant_total).toLocaleString("fr-FR")} €</p></div>
                <div className="rounded-xl bg-emerald-50 p-3"><p className="text-xs text-emerald-600/70">Part propriétaire</p><p className="font-semibold text-emerald-600">{Number(edl.cost_allocation.owner_total).toLocaleString("fr-FR")} €</p></div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-brand-soft p-5" data-testid="settlement-section">
            <h4 className="font-semibold text-brand-greenDeep flex items-center gap-2 mb-1"><FileText className="w-4 h-4" /> Proposition de frais de remise en état du logement</h4>
            <p className="text-xs text-brand-green/50 mb-3">Montant total à la charge du locataire, calculé après ventilation des frais de travaux (part locataire, vétusté déduite).</p>
            <div className="grid md:grid-cols-3 gap-3 items-end">
              <div className="text-sm">
                <p className="text-xs text-brand-green/50">Locataire</p>
                <p className="font-semibold text-brand-greenDeep" data-testid="settlement-tenant-name">{edl.tenant_name || "—"}</p>
              </div>
              <Field label="E-mail du locataire"><input value={edl.tenant_email || ""} onChange={(e) => setEdl({ ...edl, tenant_email: e.target.value })} className={inp} placeholder="pour l'envoi App-in / e-mail" data-testid="settlement-tenant-email" /></Field>
              <div className="text-sm">
                <p className="text-xs text-brand-green/50">Montant à la charge du locataire</p>
                <p className="font-semibold text-red-600 text-lg" data-testid="settlement-tenant-total">{Number(edl.cost_allocation?.tenant_total || 0).toLocaleString("fr-FR")} €</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <button onClick={downloadSettlement} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-sm font-semibold" data-testid="settlement-pdf-btn"><FileText className="w-4 h-4" /> Ouvrir le PDF du détail des travaux</button>
              <button onClick={sendSettlement} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold" data-testid="settlement-send-btn"><Send className="w-4 h-4" /> Envoyer au locataire (via la messagerie)</button>
              {edl.settlement_sent?.email && <span className="text-xs text-brand-green/45">Envoyé à {edl.settlement_sent.email}{edl.settlement_sent.channel === "app" ? " (App-in)" : edl.settlement_sent.channel === "email" ? " (e-mail)" : ""} le {(edl.settlement_sent.at || "").slice(0, 10)}</span>}
            </div>
          </div>
        </>
      )}

      {/* Bandeau : demande de correction en attente (côté référent) */}
      {!readOnly && edl.correction_requested && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex items-start gap-3" data-testid="edl-correction-request">
          <Pencil className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <b>Demande de correction</b> reçue de {edl.correction_requested.requester_name || (edl.correction_requested.party === "owner" ? "le propriétaire" : "le locataire")} :
            <span className="block text-amber-700/90 mt-0.5">« {edl.correction_requested.reason} »</span>
            <span className="block text-xs text-amber-700/70 mt-1">Utilisez « Modification de l'EDL » ci-dessous pour rouvrir, corriger, puis republier — la signature de toutes les parties sera relancée.</span>
          </div>
        </div>
      )}

      {/* Actions EDL : terminer / publier / signature groupée / modifier */}
      {!readOnly && (
        <div className="bg-white rounded-2xl border border-brand-soft p-5" data-testid="edl-actions-block">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h4 className="font-semibold text-brand-greenDeep flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Finalisation & diffusion</h4>
            {edl.status && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-brand-softer text-brand-green/70" data-testid="edl-status-badge">{edl.status === "completed" ? "Terminé" : edl.status === "published" ? "Publié" : "Brouillon"}</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={finalizeEdl} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold" data-testid="edl-finalize-btn"><CheckCircle2 className="w-4 h-4" /> État des lieux terminé</button>
            <button onClick={publishEdl} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold" data-testid="edl-publish-btn"><Search className="w-4 h-4" /> Publication de l'état des lieux</button>
            <button onClick={reopenEdl} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand-gold/50 text-brand-limeDark hover:bg-brand-gold/10 text-sm font-semibold" data-testid="edl-reopen-btn"><Pencil className="w-4 h-4" /> Modification de l'EDL</button>
          </div>
          <p className="text-xs text-brand-green/50 mt-2">« Terminé » enregistre et fige l'état des lieux. « Publication » envoie un lien de consultation (lecture seule) au locataire et au propriétaire (Messagerie App-in ou e-mail) pour revue et signature — les signatures se font dans l'ordre imposé : référent, puis locataire, puis propriétaire. « Modification » (référent) rouvre l'EDL pour correction.</p>
        </div>
      )}

      {/* Signatures 3 parties — ordre imposé : référent → locataire → propriétaire */}
      <div className="bg-white rounded-2xl border border-brand-soft p-5" data-testid="signatures-section">
        <h4 className="font-semibold text-brand-greenDeep flex items-center gap-2 mb-1"><PenLine className="w-4 h-4" /> Signatures électroniques</h4>
        <p className="text-xs text-brand-green/50 mb-3">Ordre imposé : <b>1.</b> Référent → <b>2.</b> Locataire → <b>3.</b> Propriétaire. Chaque partie ne peut signer que lorsque la précédente a signé.</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {[["referent", "1. Référent", edl.referent_name], ["tenant", "2. Locataire", edl.tenant_name], ["owner", "3. Bailleur", edl.owner_name]].map(([party, label, name]) => {
            const s = (edl.signatures || {})[party];
            const isNext = nextSigner === party;
            return (
              <div key={party} className={`rounded-xl border p-4 text-center ${isNext && !s ? "border-brand-greenDeep ring-2 ring-brand-lime" : "border-brand-soft"}`} data-testid={`sign-slot-${party}`}>
                <p className="text-sm font-semibold text-brand-greenDeep">{label}</p>
                <p className="text-xs text-brand-green/50 mb-2">{name || "—"}</p>
                {s?.signature_png_b64 ? (
                  <div>
                    <img src={s.signature_png_b64} alt="signature" className="h-16 mx-auto object-contain" />
                    <p className="text-xs text-emerald-600 flex items-center justify-center gap-1 mt-1"><CheckCircle2 className="w-3.5 h-3.5" />Signé le {(s.signed_at || "").slice(0, 10)}</p>
                    <p className="text-[9px] text-brand-green/40 mt-0.5">Cachet {s.seal_hash?.slice(0, 12)}…</p>
                  </div>
                ) : isNext ? (
                  <div className="flex flex-col items-center gap-2 mt-2">
                    <button onClick={() => setSignParty({ party, name })} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-sm font-semibold" data-testid={`sign-btn-${party}`}><PenLine className="w-4 h-4" />Signer</button>
                    <div className="flex items-center gap-2">
                      <button onClick={() => shareSign(party)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-xs font-semibold" data-testid={`qr-btn-${party}`}><QrCode className="w-3.5 h-3.5" />QR</button>
                      <button onClick={() => remindSign(party)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-xs font-semibold" data-testid={`remind-btn-${party}`}><Send className="w-3.5 h-3.5" />Relancer</button>
                    </div>
                    {(edl.reminders || {})[party]?.count > 0 && (
                      <span className="text-[10px] text-brand-green/45">Relancé {edl.reminders[party].count}× · {(edl.reminders[party].last_at || "").slice(0, 10)}</span>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1 mt-3" data-testid={`sign-waiting-${party}`}>
                    <span className="text-xs text-brand-green/40 font-medium">En attente de la signature {SIGN_LBL[SIGN_SEQ[SIGN_SEQ.indexOf(party) - 1]]}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {signParty && (
        <SignaturePad
          party={signParty.party}
          defaultName={signParty.name || ""}
          propertyId={edl.property_id}
          propertyName={edl.property_name}
          onClose={() => setSignParty(null)}
          onSubmit={(name, dataUrl) => submitSignature(signParty.party, name, dataUrl)}
        />
      )}

      {!readOnly && edl.id && (
        <EdlDispute mode="auth" eid={edl.id} canResolve={user?.role === "referent" || user?.role === "admin"} />
      )}
      {qrData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setQrData(null)}>
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 text-center" onClick={(e) => e.stopPropagation()} data-testid="qr-modal">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Signature à distance</h3>
              <button onClick={() => setQrData(null)} className="p-2 rounded-full hover:bg-brand-softer"><X className="w-5 h-5 text-brand-green/60" /></button>
            </div>
            <p className="text-sm text-brand-green/60 mb-4">Le signataire scanne ce QR code avec son mobile pour ouvrir et signer l'état des lieux.</p>
            <img src={qrData.qr} alt="QR code" className="w-52 h-52 mx-auto border border-brand-soft rounded-2xl p-2" data-testid="qr-image" />
            <div className="mt-4 flex items-center gap-2 bg-brand-softer rounded-xl px-3 py-2">
              <input readOnly value={qrData.url} className="flex-1 bg-transparent text-xs text-brand-green/70 outline-none" data-testid="qr-link" />
              <button onClick={() => { navigator.clipboard.writeText(qrData.url); toast.success("Lien copié"); }} className="p-1.5 rounded-lg hover:bg-brand-soft text-brand-greenDeep" data-testid="qr-copy"><Copy className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function SignaturePad({ party, defaultName, propertyId, propertyName, onClose, onSubmit }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);
  const [name, setName] = useState(defaultName);
  const labels = { owner: "Bailleur", tenant: "Locataire", referent: "Référent" };

  const pos = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width), y: (t.clientY - rect.top) * (c.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.lineTo(x, y); ctx.strokeStyle = "#121311"; ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.stroke(); hasDrawn.current = true; };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); hasDrawn.current = false; };
  const submit = () => {
    if (!name.trim()) { toast.error("Nom du signataire requis"); return; }
    if (!hasDrawn.current) { toast.error("Veuillez signer dans le cadre"); return; }
    onSubmit(name.trim(), canvasRef.current.toDataURL("image/png"));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="signature-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl font-semibold text-brand-greenDeep">Signature — {labels[party]}</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-brand-softer"><X className="w-5 h-5 text-brand-green/60" /></button>
        </div>
        {party === "owner" && <SavedSignatureButton propertyId={propertyId} propertyName={propertyName} onUse={(n, d) => onSubmit(n, d)} />}
        <label className="block mb-3">
          <span className="text-xs font-medium text-brand-green/70 mb-1 block">Nom du signataire</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={inp} data-testid="signer-name-input" />
        </label>
        <canvas
          ref={canvasRef} width={440} height={180}
          className="w-full border-2 border-dashed border-brand-soft rounded-xl bg-brand-softer/40 touch-none"
          data-testid="signature-canvas"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        <div className="flex items-center justify-between mt-3">
          <button onClick={clear} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-brand-soft hover:bg-brand-softer text-brand-greenDeep text-sm font-semibold"><Eraser className="w-4 h-4" />Effacer</button>
          <button onClick={submit} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold" data-testid="signature-submit-btn"><PenLine className="w-4 h-4" />Valider la signature</button>
        </div>
      </div>
    </div>
  );
}
